import React from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@mui/material';
import classNames from 'classnames';
import Typography from '@src/new-components/Typography';
import { PROJECT_PRODUCT, PROJECT_TYPE_ICONS } from '@src/constants/project';
import { isVideoSource, getProjectTypeKind } from '@src/services/project';
import useNearViewport from '@src/hooks/useNearViewport';
import { calculateElapsedDateTime } from '@src/utils/date';
import { COLOR } from '@src/styles/color';
import paperImage from '@src/assets/images/paper.svg';
import { CARD_ICONS } from './cardIcons';
import useVideoThumbnail from './useVideoThumbnail';
import RowActions from './RowActions';
import FailureWarning from './FailureWarning';
import { StyledProjectCard } from './index.style';

const ProjectCard = ({
  project,
  selected,
  menuOpen,
  onOpen,
  onToggleSelect,
  onMenuOpenChange,
  onDelete,
  limitExtracts,
}) => {
  const { t } = useTranslation();
  const typeKind = getProjectTypeKind(project);
  const { label } = PROJECT_TYPE_ICONS[typeKind];

  // A dubbing project without a video reads like a text one, so it gets the
  // paper sheet too.
  const isVideo =
    project.product === PROJECT_PRODUCT.DUBBING &&
    isVideoSource(project.source);
  const isSlide = project.product === PROJECT_PRODUCT.PRESENTATION;
  const { firstText, failed } = project;

  // A slide has a rendered page to show; a video needs a frame pulled out of it.
  const slideUrl = isSlide ? project.previewImageUrl : '';

  // Only the video path is gated. A slide's picture is an ordinary url, so the
  // `loading="lazy"` below is the browser's own job and already does this; a
  // frame has to be fetched and decoded before there is any url at all, which is
  // why the attribute cannot help there and this can. The grid appends without
  // end, so ungated it means every card ever scrolled past has paid for a moov
  // read and a GOP decode.
  const [cardRef, near] = useNearViewport();
  const frameUrl = useVideoThumbnail(
    isVideo && near ? project.source?.url : '',
    limitExtracts,
  );
  const previewUrl = isVideo ? frameUrl : slideUrl;

  // Both copies must be lazy, or the eager one fetches for the pair anyway.
  const renderPreview = (className) => (
    <img src={previewUrl} alt="" className={className} loading="lazy" />
  );

  const renderThumbnail = () => {
    // The artwork keeps its own ratio, so the card is left with empty bands.
    // A blurred copy of the same image fills them instead of flat grey.
    if (previewUrl)
      return (
        <>
          <div className="card-backdrop" aria-hidden="true">
            {renderPreview('card-backdrop-media')}
          </div>
          {renderPreview('card-media')}
        </>
      );

    // A slide or a video with nothing to show keeps the empty grey.
    if (isVideo || isSlide) return null;

    return (
      <div className="card-paper">
        <img src={paperImage} alt="" className="card-paper-image" />
        <span className="card-paper-text">
          {/* Reserves the folded corner so the first line flows around it. */}
          <span className="card-paper-fold" aria-hidden="true" />
          {firstText || t('emptyContent')}
        </span>
      </div>
    );
  };

  return (
    <StyledProjectCard
      ref={cardRef}
      className={classNames({ 'card-armed': selected || menuOpen })}
    >
      <div
        className="card-thumbnail"
        onClick={() => !failed && onOpen(project)}
        role="button"
        tabIndex={0}
      >
        {renderThumbnail()}

        {/* Both sit over the picture, so neither takes room from the card. */}
        <Checkbox
          className="card-select"
          // Without this the ripple takes the theme's primary, which is yellow.
          color="default"
          checked={selected}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggleSelect(project)}
        />
        <div className="card-actions">
          <RowActions
            open={menuOpen}
            onOpenChange={(next) => onMenuOpenChange(project.id, next)}
            onDelete={() => onDelete(project)}
          />
        </div>
      </div>

      <div className="card-info">
        <div className="card-title-row">
          <img
            src={CARD_ICONS[typeKind]}
            alt={t(label)}
            className="card-type-icon"
          />
          <Typography
            className="card-title"
            variant="body-14-medium"
            color={COLOR.label2[100]}
          >
            {project.title}
          </Typography>
          {failed && <FailureWarning placement="top" />}
        </div>
        <Typography variant="body-14-regular" color={COLOR.label2[64]}>
          {calculateElapsedDateTime(project.updatedAt, t)}
        </Typography>
      </div>
    </StyledProjectCard>
  );
};

export default React.memo(ProjectCard);
